<script lang="ts">
import Vue from "vue";
import Component from "vue-class-component";
import { Prop, Model, Watch, Ref } from "vue-property-decorator";

import { AbstractDetailsValueRepresentation } from "@/lib/apis/replicable/models";

import { filter } from "@/filters/DateFilter";

import DetailsValueSource from "@/components/form/vues/details/DetailsValueSource.vue";
import { Modal as NekoModal } from "@/components/modal";

import DetailsValueView from "./details/DetailsValueView.vue";
import EditButtons from "./details/EditButtons.vue";
import DetailsValueInput from "./details/DetailsValueInput.vue";
import { Field as NekoField, Select as NekoSelect, Input as NekoInput } from "../";

@Component({
	components: {
		DetailsValueSource,
		DetailsValueInput,
		DetailsValueView,
		EditButtons,
		NekoField,
		NekoSelect,
		NekoInput,
		NekoModal,
	}
})
export default class DetailsComponent extends Vue {
	@Model("input", { required: true })
	public readonly detailsValues!: AbstractDetailsValueRepresentation[];

	@Prop({ default: null })
	public readonly label!: string | null;

	@Prop({ default: null })
	public readonly description!: string | null;

	@Prop({ default: true })
	public readonly horizontal!: boolean;

	@Prop({ default: true })
	public readonly showDate!: boolean;

	@Prop({ default: true })
	public readonly editable!: boolean;

	@Prop({ default: true })
	public readonly autoValidate!: boolean;

	@Prop({ default: false })
	public readonly autoOpenEdit!: boolean;

	@Ref()
	private readonly editModal!: NekoModal;

	public detailsValuesEdited: AbstractDetailsValueRepresentation[] = [];
	public fieldEditing: boolean = false;
	public lastDetailsValue: AbstractDetailsValueRepresentation | null = null;
	public showPrevious: boolean = false;
	public date = filter;

	/** Liste des premières données supplémentaires de valeurs identiques triées antéchronologiquement */
	public get mergedDetailsValues(): AbstractDetailsValueRepresentation[] {
		return this.sortedDetailsValues.reduce<AbstractDetailsValueRepresentation[]>((result, detailsValue, index) => {
			if (index === 0 || result[result.length - 1].value !== detailsValue.value) {
				result.push(detailsValue);
			}

			return result;
		}, [])
			.reverse();
	}

	/** Liste des données supplémentaires triées antéchronologiquement */
	public get reversedDetailsValues(): AbstractDetailsValueRepresentation[] {
		return this.sortedDetailsValues.slice().reverse();
	}

	/** Liste des données supplémentaires triées chronologiquement */
	public get sortedDetailsValues(): AbstractDetailsValueRepresentation[] {
		return this.detailsValues.slice().sort((a, b) => {
			if (a.datePeriod && b.datePeriod) {
				return new Date(a.datePeriod).getTime() - new Date(b.datePeriod).getTime();
			}
			if (a.datePeriod && !b.datePeriod) {
				return -1;
			}
			if (!a.datePeriod && b.datePeriod) {
				return 1;
			}

			return 0;
		});
	}

	public created() {
		this.onModelUpdated();
	}

	public onCopy(from: "DSN" | "PAY_SLIP", detailsValue: AbstractDetailsValueRepresentation): void {
		if (!this.editable) {
			return;
		}

		switch (from) {
			case "DSN":
				detailsValue.value = detailsValue.dsnValue;
				detailsValue.source = from;
				break;
			case "PAY_SLIP":
				detailsValue.value = detailsValue.paySlipValue;
				detailsValue.source = from;
				break;
			default:
				break;
		}

		this.detailsValuesEdited = [detailsValue];
		this.onValidate();
	}

	public onDelete(detailsValue: AbstractDetailsValueRepresentation) {
		const index = this.detailsValuesEdited.findIndex(d => d.id === detailsValue.id);

		if (index !== -1) {
			this.detailsValuesEdited.splice(index, 1);
		}
	}

	public onDeleteAll() {
		this.detailsValuesEdited.splice(0);
	}

	public async onFieldEdit(edit: boolean) {
		if (!edit) {
			this.detailsValuesEdited = [];
		} else if (!this.fieldEditing) {
			this.detailsValuesEdited = this.reversedDetailsValues.map(d => ({ ...d }));

			if (this.detailsValuesEdited.length > 1) {
				this.editModal.show();
			}
		}

		this.fieldEditing = edit;
	}

	public onValidate() {
		this.fieldEditing = false;
		this.$emit("input", this.detailsValuesEdited);

		if (this.editModal.shown) {
			this.editModal.hide();
		}
	}

	public toggleShowPrevious(): void {
		if (!this.fieldEditing) {
			this.showPrevious = !this.showPrevious;
		}
	}

	@Watch("detailsValues")
	private onModelUpdated() {
		if (this.autoOpenEdit && this.detailsValues.some(d => d.id == null || d.value == null || d.value.length === 0)) {
			this.onFieldEdit(true);
		}

		if (this.mergedDetailsValues.length) {
			this.lastDetailsValue = this.mergedDetailsValues[0];
		} else {
			this.lastDetailsValue = null;
		}
	}

	@Watch("fieldEditing")
	private onFieldEditing(value: boolean) {
		if (this.fieldEditing && this.detailsValuesEdited.length > 1) {
			this.editModal.show();
		}
	}
}
</script>

<i18n src="../i18n/Details.json"></i18n>

<template>
	<div class="details-data" :class="{ 'has-label-hidden': !label }">
		<div v-if="label" class="details-label">
			<span
				v-if="description && description !== label"
				class="description has-tooltip-right has-tooltip-multiline mr-2"
				v-tooltip.right="description"
			>
				<span class="fas fa-info-circle"></span>
			</span>
			<label class="label mb-0 is-line-limited-2" v-tooltip="label">{{ label }}</label>
			<edit-buttons
				v-if="editable"
				:field-editing="fieldEditing"
				@start-edit="onFieldEdit(true)"
				@end-edit="onFieldEdit(false)"
				@validate="onValidate()"
			>
				<router-link v-if="lastDetailsValue && lastDetailsValue['@type'] === 'EMPLOYEE'"
				             :to="{name: 'details.employee.edit', params : {id: lastDetailsValue.detailsId }}"
				             class="button is-small" target="_blank">
					<span class="icon"><span class="fa fa-external-link"></span></span>
				</router-link>
				<router-link v-else-if="lastDetailsValue && lastDetailsValue['@type'] === 'INSTITUTION'"
				             :to="{name: 'details.institution.edit', params : {id: lastDetailsValue.detailsId }}"
				             class="button is-small" target="_blank">
					<span class="icon"><span class="fa fa-external-link"></span></span>
				</router-link>
			</edit-buttons>
		</div>

		<div class="details-value-container">
			<div
				class="is-details-value"
				:class="{
					'has-siblings': !fieldEditing && mergedDetailsValues.length > 1,
					'is-empty': fieldEditing && !detailsValuesEdited.length,
					'is-extended': showPrevious
				}"
				@click="toggleShowPrevious"
			>
				<template v-if="!fieldEditing || detailsValuesEdited.length > 1">
					<details-value-view
						:details-value="lastDetailsValue"
						:show-date="mergedDetailsValues.length > 1"
						@copy="onCopy"
					/>
					<div v-if="mergedDetailsValues.length > 1 && showPrevious" class="details-value-siblings">
						<template v-for="(detailsValue, index) in mergedDetailsValues">
							<details-value-view
								v-if="detailsValue !== lastDetailsValue"
								:key="index"
								:detailsValue="detailsValue"
								@copy="onCopy"
							/>
						</template>
					</div>
				</template>

				<span v-else-if="fieldEditing && detailsValuesEdited.length === 0" v-t="'no.data'"></span>

				<template v-else>
					<div v-for="(detailsValue, index) in detailsValuesEdited" :key="'editing-' + index" class="field">
						<details-value-input :details-value="detailsValue" @delete="onDelete(detailsValue)"/>
					</div>
				</template>
			</div>

			<edit-buttons
				v-if="editable && !label"
				:field-editing="fieldEditing"
				@start-edit="onFieldEdit(true)"
				@end-edit="onFieldEdit(false)"
				@validate="onValidate()"
			/>
		</div>

		<neko-modal ref="editModal" size="medium" @close="onFieldEdit(false)">
			<template #title>
				Édition des valeurs pour : {{ detailsValues[0].label }}
			</template>
			<template #default>
				<div class="buttons is-right mb-2 mt-4">
					<button class="button is-danger is-outlined is-small" type="button" @click="onDeleteAll()">
						<span class="icon"><span class="fas fa-trash"></span></span>
						<span v-t="'action.deleteAll'"></span>
					</button>
				</div>

				<table class="table is-fullwidth is-striped is-hoverable">
					<thead>
					<tr>
						<th>Période</th>
						<th class="is-narrow"></th>
						<th>Valeur</th>
					</tr>
					</thead>
					<tbody>
					<tr v-show="detailsValuesEdited.length === 0" class="has-text-centered has-background-light">
						<td colspan="3">Aucune donnée</td>
					</tr>
					<tr v-for="dv in detailsValuesEdited" :key="dv.detailsId + '-' + dv.datePeriod">
						<td>
							<neko-input type="date" v-model="dv.datePeriod"/>
						</td>
						<td>
							<details-value-source :source="dv.source"/>
						</td>
						<td>
							<details-value-input :details-value="dv" @delete="onDelete(dv)"/>
						</td>
					</tr>
					</tbody>
				</table>
			</template>
			<template #footer>
				<button @click.prevent="onValidate()" class="button is-primary">Valider</button>
			</template>
		</neko-modal>
	</div>
</template>
